/**
 * One figure, read over the window the tab asks about.
 *
 * WHY THIS EXISTS. Every widget printed six periods as a `grid-cols-3` of
 * equal tiles -- today, this week, this month, and the rolling 24 hours, 7
 * days and 30 days. Six numbers of identical weight is not an answer, it is
 * the reader being asked to find one. Filtering that grid down to a single
 * period made it worse rather than better: the tiles were sized for six, so
 * one left a full-width box with a small square stranded beside empty space,
 * and "Month · 0" alone says nothing at all because there is nothing to read
 * it against.
 *
 * So a tab shows the window it was asked about and nothing else: one number,
 * large, and under it the same figure drawn day by day across that window --
 * month by month for All time. The chart is what a lone number is read
 * against.
 *
 * Without a `PeriodProvider` -- anywhere outside the dashboard -- it falls
 * back to an even grid of everything it was given, which is what these widgets
 * did before and what their other callers still expect.
 */
import { Chart, Stat, StatLabel, StatNumber, dayLabel } from '~/shared/ui'
import { Period, periodKey, usePeriod, useRange } from './period'

/** The calendar windows, in order, with what to call them. */
export const CALENDAR: [key: Period, label: string][] = [
    ['today', 'Today'],
    ['week', 'This week'],
    ['month', 'This month'],
]

/** One line on the chart: a day -> value history as the endpoint returns it. */
export type FigureLine = {
    label: string
    colour: string
    history?: Record<string, number | null>
}

/** The days a window covers, given the days a history has, oldest first.
 *
 *  The newest key is today in the zone the endpoint counted in, so the window
 *  is worked out from that date rather than from the device's clock: a viewer
 *  west of the zone would otherwise lose or gain a day at each end.
 *
 *  Today is one point, and one point is not a line, so Today draws the last
 *  seven days ending today -- the day being asked about, with a week of
 *  ordinary days to read it against.
 */
const windowDays = (days: string[], period: Period): string[] => {
    // All time and a custom range were fetched as exactly the buckets to draw.
    if (period === 'all' || period === 'custom') return days
    const today = days[days.length - 1]
    if (!today) return []
    const [y, m, d] = today.split('-').map(Number)
    const count =
        period === 'today' ? 7 : period === 'week' ? ((new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7) + 1 : d
    return days.slice(-count)
}

/** The window's days drawn as one chart, every line on the same days. */
const WindowChart = ({ period, lines, options }: { period: Period; lines: FigureLine[]; options?: object }) => {
    const byDay = lines.map((line) =>
        Object.fromEntries(Object.entries(line.history ?? {}).map(([key, value]) => [dayLabel(key), value]))
    )
    const all = [...new Set(byDay.flatMap((series) => Object.keys(series)))].sort()
    const labels = windowDays(all, period)
    if (!labels.length) return null

    return (
        <Chart.TimeSeries
            className='h-48 sm:h-56'
            data={{
                labels,
                datasets: lines.map((line, i) => ({
                    label: line.label,
                    data: labels.map((day) => (byDay[i][day] == null ? null : Number(byDay[i][day]))),
                    borderColor: line.colour,
                    backgroundColor: line.colour,
                })),
            }}
            options={
                period === 'all'
                    ? {
                          ...(options ?? {}),
                          scales: {
                              ...((options as any)?.scales ?? {}),
                              x: {
                                  type: 'time',
                                  time: { unit: 'month', tooltipFormat: 'MMM YYYY' },
                                  ticks: { autoSkip: true, maxTicksLimit: 8, maxRotation: 0, font: { size: 10 } },
                                  grid: { display: false },
                              },
                          },
                      }
                    : options
            }
        />
    )
}

type Props = {
    /** Every period this figure can be read over, with its label. */
    periods: [key: string, label: string][]
    loading?: boolean
    /** The number itself. Undefined renders as the loading pulse. */
    value: (key: string) => any
    /** The smaller line under it -- "12 deposits", "(3 activated)". */
    note?: (key: string) => any
    /** A standing total shown first, where one means something. */
    total?: { label: string; value: any; note?: any; as?: any }
    /** Drawn under the headline over the chosen window. Dashboard only. */
    chart?: { lines: FigureLine[]; options?: object }
}

export const PeriodFigures = ({ periods, loading, value, note, total, chart }: Props) => {
    const period = usePeriod()
    const range = useRange()
    const labels = new Map(periods)

    /* No window chosen: the even grid, as before. */
    if (!period) {
        return (
            <section className='grid w-full grid-cols-2 gap-2 sm:grid-cols-3'>
                {total && (
                    <Stat.Gray as={total.as} loading={loading} className='col-span-2 sm:col-span-3'>
                        <StatLabel className='text-amber-500'>{total.label}</StatLabel>
                        <StatNumber>{total.value}</StatNumber>
                        {total.note && <StatLabel className='text-white/40'>{total.note}</StatLabel>}
                    </Stat.Gray>
                )}
                {periods.map(([key, label]) => (
                    <Stat.Gray key={key} loading={loading}>
                        <StatLabel className='text-amber-500'>{label}</StatLabel>
                        <StatNumber>{value(key)}</StatNumber>
                        {note && <StatLabel className='text-white/40'>{note(key)}</StatLabel>}
                    </Stat.Gray>
                ))}
            </section>
        )
    }

    const key = periodKey(period)
    const label =
        period === 'all' ? 'All time' : range ? `${range.from} to ${range.to}` : (labels.get(period) ?? period)
    // A figure whose lifetime number is handed over separately (`total`)
    // answers All time from that rather than from `value`.
    const headline = period === 'all' && total ? total.value : value(key)
    const subline = period === 'all' && total ? total.note : note?.(key)

    return (
        <Stat.Gray loading={loading} className='w-full py-6'>
            {/* The window is named above the number, so a screenshot of this
                box is still answerable on its own. */}
            <StatLabel className='uppercase tracking-widest text-amber-500'>{label}</StatLabel>
            <StatNumber className='text-3xl sm:text-4xl md:text-5xl'>{headline}</StatNumber>
            {subline && <StatLabel className='text-white/50'>{subline}</StatLabel>}
            {chart && (
                <div className='w-full px-2 pt-4'>
                    <WindowChart period={period} lines={chart.lines} options={chart.options} />
                </div>
            )}
        </Stat.Gray>
    )
}
